import {
  HorizontalOriginOption, OriginOptions, Overlay, VerticalOriginOption
} from '@tableau/widgets';
import { FlexDirectionProperty } from 'csstype';
import * as React from 'react';
import { ChromeTooltipArrow, Direction, Height } from './ChromeTooltipArrow';
import { ChromeTooltipBox } from './ChromeTooltipBox';
import { assertNever, isHorizontalSide, isVerticalSide } from './ChromeTooltipUtility';
import { ChromeTooltipSide } from './ChromeTooltipWidget';

export interface ChromeTooltipDisplayProps {
  readonly content: JSX.Element;
  readonly width?: number;
  readonly targetElement: HTMLElement;
  readonly handleRef: React.Ref<HTMLDivElement>;
  readonly close: () => void;
  readonly positioned: boolean;
  readonly side: ChromeTooltipSide;
  readonly offset: number;
  // TODO remove after unifying stacking strategy in viz client, see feature 579115
  readonly zIndex?: number;
}

export function ChromeTooltipDisplay(props: ChromeTooltipDisplayProps): React.ReactElement<ChromeTooltipDisplayProps> {
  const getAnchorClientRect = () => {
    const rect = props.targetElement.getBoundingClientRect();
    const y = getTopFromOverlaidPosition(props.side, props.offset);
    const x = getLeftFromOverlaidPosition(props.side, props.offset);
    return {
      top: rect.top + y,
      bottom: rect.bottom + y,
      left: rect.left + x,
      right: rect.right + x,
    } as ClientRect;
  };
  return (
    <Overlay getAnchorClientRect={getAnchorClientRect}
      zIndex={props.zIndex}
      anchorOrigin={getAnchorOrigin(props.side)}
      stackedOrigin={getStackedOrigin(props.side)}>
      {/* TFSID 701344:Certified data source hover tooltip renders off the page in IE.
        * IE11 bugs when dealing with display:flex and elements that have intrinsic aspect ratios,
        * requiring an extra container here. See IE11 display:flex known issues for more.
        * https://github.com/philipwalton/flexbugs#5-column-flex-items-dont-always-preserve-intrinsic-aspect-ratios
        */}
      <div ref={props.handleRef}
        onMouseLeave={props.close}>
        <div style={getContainerStyle(props.positioned, props.side)}>
          <ChromeTooltipBox content={props.content} width={props.width} />
          <ChromeTooltipArrow direction={getDirection(props.side)}
            style={getArrowStyle(props.side, props.offset)} />
        </div>
      </div>
    </Overlay>
  );
}

function getAnchorOrigin(side: ChromeTooltipSide): OriginOptions {
  switch (side) {
    case 'Top': return { horizontal: HorizontalOriginOption.centerX, vertical: VerticalOriginOption.top };
    case 'Bottom': return { horizontal: HorizontalOriginOption.centerX, vertical: VerticalOriginOption.bottom };
    case 'Left': return { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.centerY };
    case 'Right': return { horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.centerY };
    default: return assertNever(side);
  }
}

function getStackedOrigin(side: ChromeTooltipSide): OriginOptions {
  switch (side) {
    case 'Top': return { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.bottom };
    case 'Bottom': return { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.top };
    case 'Left': return { horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.top };
    case 'Right': return { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.top };
    default: return assertNever(side);
  }
}

function getTopFromOverlaidPosition(side: ChromeTooltipSide, offset: number): number {
  return isHorizontalSide(side) ? -1 * offset : 0;
}

function getLeftFromOverlaidPosition(side: ChromeTooltipSide, offset: number): number {
  return isVerticalSide(side) ? -1 * offset : 0;
}

function getContainerStyle(positioned: boolean, side: ChromeTooltipSide): React.CSSProperties {
  return {
    visibility: positioned ? 'visible' : 'hidden',
    display: 'flex',
    flexDirection: getFlexDirection(side),
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center'
  };
}

function getFlexDirection(side: ChromeTooltipSide): FlexDirectionProperty {
  switch (side) {
    case 'Top': return 'column';
    case 'Bottom': return 'column-reverse';
    case 'Left': return 'row';
    case 'Right': return 'row-reverse';
    default: return assertNever(side);
  }
}

function getDirection(side: ChromeTooltipSide): Direction {
  switch (side) {
    case 'Top': return 'Down';
    case 'Bottom': return 'Up';
    case 'Left': return 'Right';
    case 'Right': return 'Left';
    default: return assertNever(side);
  }
}

function getArrowStyle(side: ChromeTooltipSide, offset: number): React.CSSProperties {
  let mainAxisOffset = -1.2;
  let crossAxisOffset = offset - Height / 2;
  let style: React.CSSProperties = { alignSelf: 'flex-start' };
  switch (side) {
    case 'Top':
      style.marginTop = mainAxisOffset;
      style.marginLeft = crossAxisOffset;
      break;
    case 'Bottom':
      style.marginBottom = mainAxisOffset;
      style.marginLeft = crossAxisOffset;
      break;
    case 'Left':
      style.marginLeft = mainAxisOffset;
      style.marginTop = crossAxisOffset;
      break;
    case 'Right':
      style.marginRight = mainAxisOffset;
      style.marginTop = crossAxisOffset;
      break;
    default:
      assertNever(side);
  }
  return style;
}
